import React, { useState, useEffect, useCallback } from 'react';
import DipSwitchHelper from './DipSwitchHelper';
import { FaShareAlt, FaPlusCircle } from 'react-icons/fa';
import { GrPowerReset } from 'react-icons/gr';

interface DeviceConfig {
  id: string;
  name: string;
  switchCount: number;
  decimalValue: number;
}

const SupervisorView: React.FC = () => {
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [devices, setDevices] = useState<DeviceConfig[]>([]);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [savedReadOnlyLink, setSavedReadOnlyLink] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Add a default device in Simple Mode
  const createDefaultDevice = () => {
    const defaultDevice: DeviceConfig = {
      id: Date.now().toString(),
      name: `Default Device`,
      switchCount: 8,
      decimalValue: 0,
    };
    return defaultDevice;
  };

  // Initialize devices when switching modes
  useEffect(() => {
    if (isAdvancedMode && devices.length === 0) {
      const defaultDevice = createDefaultDevice();
      setDevices([defaultDevice]);
      setCurrentDeviceId(defaultDevice.id);
    } else if (!isAdvancedMode) {
      // When switching from Advanced Mode to Simple Mode, reset to default device
      const defaultDevice = createDefaultDevice();
      setDevices([defaultDevice]);
      setCurrentDeviceId(defaultDevice.id);
    }
  }, [isAdvancedMode, devices.length]);

  // Add a new device with a prefilled name in Advanced Mode
  const addDevice = useCallback(() => {
    const newDevice: DeviceConfig = {
      id: Date.now().toString(),
      name: `Device ${devices.length + 1}`,
      switchCount: 8,
      decimalValue: 0,
    };
    setDevices(prevDevices => [...prevDevices, newDevice]);
    setCurrentDeviceId(newDevice.id);
  }, [devices.length]);

  // Remove a specific device by ID
  const removeDevice = (deviceId: string) => {
    setDevices(prevDevices => prevDevices.filter(device => device.id !== deviceId));
    if (currentDeviceId === deviceId) {
      setCurrentDeviceId(null); // Clear current device if it was the one being deleted
    }
  };

  // Reset all devices
  const resetDevices = () => {
    setDevices([]);
    setCurrentDeviceId(null);
    setSavedReadOnlyLink(null);
  };

  // Update device configurations dynamically
  const updateDevice = (updatedConfig: Partial<DeviceConfig>, deviceId: string) => {
    setDevices((prevDevices) =>
      prevDevices.map((device) =>
        device.id === deviceId ? { ...device, ...updatedConfig } : device
      )
    );
  };



  // Generate the read-only link
  const generateReadOnlyLink = useCallback(() => {
    const baseUrl = window.location.origin;
    const overviewData = devices.map(device => ({
      name: device.name,
      switchCount: device.switchCount,
      decimalValue: device.decimalValue,
    }));
    const encodedConfig = btoa(JSON.stringify(overviewData));
    return `${baseUrl}/technician/${encodedConfig}`;
  }, [devices]);
  

  // Save the shareable link to clipboard
  const handleShare = () => {
    const link = generateReadOnlyLink();
    setSavedReadOnlyLink(link);
    navigator.clipboard.writeText(link);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500); // Hide the "Copied" tooltip after 1.5 seconds
  };

  const currentDevice = devices.find(device => device.id === currentDeviceId);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex flex-col space-y-4 mb-6">
        {/* Mode switch and Share button row */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <button
            onClick={() => setIsAdvancedMode(!isAdvancedMode)}
            className="bg-gray-200 text-black px-4 py-2 rounded hover:bg-gray-300 transition w-full sm:w-auto"
          >
            {isAdvancedMode ? 'Switch to Simple Mode' : 'Switch to Advanced Mode'}
          </button>
          <button
            onClick={handleShare}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition flex items-center justify-center w-full sm:w-auto"
          >
            <FaShareAlt className="mr-2" /> Share
          </button>
        </div>

        {/* Add Device and Reset buttons row (visible only in Advanced Mode) */}
        {isAdvancedMode && (
          <div className="flex space-x-4">
            <button
              onClick={addDevice}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center justify-center flex-1 sm:flex-initial"
            >
              <FaPlusCircle className='mr-2' /> Add Device
            </button>
            <button
              onClick={resetDevices}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition flex items-center justify-center flex-1 sm:flex-initial"
            >
              <GrPowerReset className="mr-2" /> Reset
            </button>
          </div>
        )}
      </div>
      
      {isCopied && (
        <div className="fixed top-4 right-4 bg-gray-800 text-white py-2 px-4 rounded-lg shadow-lg transition-opacity opacity-100">
          Copied to Clipboard!
        </div>
      )}

      <div className="flex flex-col sm:flex-row mb-6">
        {isAdvancedMode && (
          <div className="w-full sm:w-1/3 pr-0 sm:pr-4 mb-6 sm:mb-0">
            <h2 className="text-xl font-semibold mb-4">Devices</h2>
            <ul className="space-y-4">
              {devices.map((device) => (
                <li
                  key={device.id}
                  className={`cursor-pointer p-2 rounded ${
                    currentDeviceId === device.id ? "bg-gray-200" : ""
                  }`}
                  onClick={() => setCurrentDeviceId(device.id)}
                >
                  <input
                    type="text"
                    value={device.name}
                    onChange={(e) =>
                      updateDevice({ name: e.target.value }, device.id)
                    }
                    className="w-full p-1 border rounded mb-2"
                  />
                  <button
                    className="text-red-500 text-sm hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDevice(device.id);
                    }}
                  >
                    Remove Device
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`w-full ${isAdvancedMode ? 'sm:w-2/3' : ''}`}>
          {currentDevice ? (
            <div className="p-4 sm:p-6 bg-white rounded-lg shadow-md mb-6">
              <h2 className="text-xl font-semibold mb-4">
                {currentDevice.name} Configuration
              </h2>
              <DipSwitchHelper
                key={currentDevice.id}
                switchCount={currentDevice.switchCount}
                initialValue={currentDevice.decimalValue}
                onConfigChange={(newConfig) =>
                  updateDevice(newConfig, currentDevice.id)
                }
                editable={true}
              />
            </div>
          ) : (
            <DipSwitchHelper
              switchCount={8}
              initialValue={0}
              editable={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SupervisorView;
